import { useEffect } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  TextField,
  alpha,
  useTheme,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { moviefoneNotesState, MoviefoneNotes } from 'states/moviefone';
import { $MoviefoneChatBody as MoviefoneChatBody } from 'gen/stream-api/requests/schemas.gen';

const textFieldSx = {
  flex: 1,
  marginTop: 4,
  display: 'flex',
  '& .MuiInputBase-root': {
    height: '100%',
    alignItems: 'flex-start',
  },
  '& .MuiInputBase-input': {
    height: '100% !important',
    overflow: 'auto',
  },
};

interface MoviefoneNotesDrawerProps {
  open: boolean;
  onClose: () => void;
}

const MoviefoneNotesDrawer = ({ open, onClose }: MoviefoneNotesDrawerProps) => {
  const [notes, setNotes] = useRecoilState(moviefoneNotesState);
  const resetNotes = useResetRecoilState(moviefoneNotesState);
  const theme = useTheme();

  const updateField = (
    field: keyof Omit<MoviefoneNotes, 'dirty'>,
    value: string,
  ) => {
    setNotes((prev) => ({ ...prev, [field]: value, dirty: true }));
  };

  useEffect(() => {
    if (notes.primaryPrompt == null) {
      setNotes((prev) => ({
        ...prev,
        primaryPrompt:
          MoviefoneChatBody.properties.primary_prompt_override.example,
      }));
    }
    if (notes.gatekeeperPrompt == null) {
      setNotes((prev) => ({
        ...prev,
        gatekeeperPrompt:
          MoviefoneChatBody.properties.gatekeeper_prompt_override.example,
      }));
    }
  }, [notes, setNotes]);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { width: '90vw' } }}
    >
      <Stack sx={{ height: '100%', p: 2, gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            aria-label="reset notes"
            size="small"
            onClick={resetNotes}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              },
              flexShrink: 0,
              transition: 'box-shadow 1s',
            }}
          >
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </Box>
        <Stack sx={{ flex: 1, minHeight: 0, gap: 5 }}>
          <label htmlFor="gatekeeper-model">Gatekeeper Model</label>
          <input
            id="gatekeeper-model"
            type="text"
            value={notes.gatekeeperModel}
            onChange={(e) => updateField('gatekeeperModel', e.target.value)}
          />
          <TextField
            multiline
            fullWidth
            label="Gatekeeper Prompt"
            value={notes.gatekeeperPrompt}
            onChange={(e) => updateField('gatekeeperPrompt', e.target.value)}
            sx={textFieldSx}
          />
        </Stack>
        <Stack sx={{ flex: 1, minHeight: 0, gap: 1 }}>
          <label htmlFor="primary-model">Primary Model</label>
          <input
            id="primary-model"
            type="text"
            value={notes.primaryModel}
            onChange={(e) => updateField('primaryModel', e.target.value)}
          />
          <TextField
            multiline
            fullWidth
            label="Primary Prompt"
            value={notes.primaryPrompt}
            onChange={(e) => updateField('primaryPrompt', e.target.value)}
            sx={textFieldSx}
          />
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default MoviefoneNotesDrawer;
